<template>
  <div>
    <div v-if="showPrimary">
      <div :style="getRiskStyle(selectedPrimaryRisk, true)">
        <RiskRatingDefinition
          :riskCategoryInfo="getRiskCategoryInfo(selectedPrimaryRisk)"
        >
          <div
            :style="getRiskStyle(selectedPrimaryRisk)"
            class="hyp-risk-icon cursor-pointer pull-left mt-1 mx-1"
          ></div>
        </RiskRatingDefinition>
        <div class="pull-left">
          {{ selectedPrimaryRisk.riskCategoryNameDisplay }}:
        </div>
        <div class="pull-left pr-6 pl-1">
          {{ getRiskLevel(selectedPrimaryRisk) }}
        </div>
        <div class="clearfix"></div>
      </div>
    </div>
    <v-row v-if="showSubrisks">
      <v-col
        class="pb-0 pt-1"
        cols="12"
        v-if="
          selectedPrimaryRisk &&
          selectedPrimaryRisk.riskCategoryNameDisplay &&
          risksSub &&
          risksSub.length
        "
      >
        <v-row v-if="showTitle"
          ><v-col cols="12">
            <span class="details-subheading application-heading"
              >{{ selectedPrimaryRisk.riskCategoryNameDisplay }} Sub
              Ratings</span
            >
          </v-col></v-row
        >
        <div class="well" :class="{ 'hyp-risk-grid-2': showGridView }">
          <div
            v-for="(risk, index) in risksSub"
            :key="selectedPrimaryRisk.riskCategoryId + risk.id + index"
            class="my-1 subrisk-container"
          >
            <div>
              <RiskRatingDefinition
                :riskCategoryInfo="getRiskCategoryInfo(risk)"
              >
                <div
                  :style="getSubRiskStyle(risk)"
                  class="hyp-risk-icon cursor-pointer pull-left mt-1 mx-1"
                ></div>
              </RiskRatingDefinition>
              <div class="hyp-risk-type pull-left">{{ risk.type }}:</div>
              <div class="pull-left pl-1">{{ getSubRiskLevel(risk) }}</div>
              <div class="clearfix"></div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashOrderBy from "lodash/orderBy";
import lodashRemove from "lodash/remove";

import { getRiskCategories } from "@/services/controlRisks/risks.service";
import {
  RISK_LEVELS,
  RISK_RATING_PRIMARIES,
  RISK_RATINGS_AVAILABLE,
} from "@/common/constants/controlRisks/risks";
import { hasPlusCyberAccess } from "@/services/user/user.service";

export default {
  name: "RiskRatingSub",
  components: {
    RiskRatingDefinition: () =>
      import("@/components/controlRisks/risks/RiskRatingDefinition"),
  },
  props: {
    title: {
      type: String,
      required: false,
    },
    ratings: {
      type: Array,
      required: true,
    },
    parentRatings: {
      type: Array,
      required: false,
    },
    selectedPrimaryRisk: {
      type: Object,
      required: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    showPrimary: {
      type: Boolean,
      default: true,
    },
    showGridView: {
      type: Boolean,
      default: false,
    },
    showSubrisks: {
      type: Boolean,
      default: true,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    const availableRiskTypes = [];
    RISK_RATINGS_AVAILABLE.forEach((r) => {
      if (!r.parentId) {
        availableRiskTypes.push(r);
      }
    });

    if (!hasPlusCyberAccess()) {
      lodashRemove(availableRiskTypes, (r) => {
        return r.id === RISK_RATING_PRIMARIES.cyber;
      });
    }

    if (lodashGet(this.params, "riskParentCategories")) {
      const riskParentCategories = lodashGet(
        this.params,
        "riskParentCategories"
      ).split(",");
      lodashRemove(availableRiskTypes, (r) => {
        return !riskParentCategories.includes(r.id);
      });
    }

    return {
      availableRiskTypes: availableRiskTypes,
      riskTypes: null,
      risks: null,
      risksSub: null,
    };
  },
  computed: {
    riskTypesPrimary() {
      if (this.riskTypes && Array.isArray(this.riskTypes)) {
        let rTypes = [];
        this.riskTypes.forEach((rt) => {
          if (!rt.parentId) {
            rTypes.push(rt);
          }
        });
        return rTypes;
      }
      return null;
    },
  },
  methods: {
    async getFilteredRiskTypes() {
      let rtArr = [];
      const riskTypes = await getRiskCategories();
      riskTypes.forEach((riskType) => {
        this.availableRiskTypes.forEach((rt) => {
          if (riskType.id === rt.id || riskType.parentId === rt.id) {
            rtArr.push(riskType);
          }
        });
      });
      return rtArr;
    },
    getRiskCategoryInfo(risk) {
      return {
        riskCategoryId: this.selectedPrimaryRisk.riskCategoryId,
        riskValue: risk.value ? risk.value : risk.rateValue,
        riskCategoryNameDisplay: this.selectedPrimaryRisk
          .riskCategoryNameDisplay,
      };
    },
    getRiskLevel(risk) {
      if (risk && risk.rateValue) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.rateValue]);
        return lodashGet(rating, "name");
      }
      return null;
    },
    getSubRiskLevel(risk) {
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        return lodashGet(rating, "name");
      }
      return null;
    },
    getSubRiskStyle(risk) {
      let isSameAsParent = false;
      const id = risk.id;
      const level = risk.value;
      for (
        let i = 0;
        this.parentRatings && i < this.parentRatings.length;
        i++
      ) {
        const checkLevel = this.parentRatings[i].rateValue;
        const checkId = this.parentRatings[i].riskCategoryId;
        if (id === checkId && level === checkLevel) {
          isSameAsParent = true;
        }
      }
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        const color = isSameAsParent ? "#CBCBCB" : lodashGet(rating, "bgColor");

        return {
          "background-color": color,
          height: "14px",
          width: "14px",
        };
      }
      return null;
    },
    getRiskStyle(risk, boxStyle) {
      let isSameAsParent = false;
      const id = risk.riskCategoryId;
      const level = risk.rateValue;
      for (
        let i = 0;
        this.parentRatings && i < this.parentRatings.length;
        i++
      ) {
        const checkLevel = this.parentRatings[i].rateValue;
        const checkId = this.parentRatings[i].riskCategoryId;
        if (id === checkId && level === checkLevel) {
          isSameAsParent = true;
        }
      }

      if (risk && risk.rateValue) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.rateValue]);
        const color = isSameAsParent ? "#CBCBCB" : lodashGet(rating, "bgColor");
        if (boxStyle) {
          return {};
        }
        return {
          "background-color": color,
          height: "14px",
          width: "14px",
        };
      }
      return null;
    },
    getSubRatings() {
      this.risksSub = null;
      if (this.selectedPrimaryRisk) {
        let riskTypesSub = [];
        this.riskTypes.forEach((rt) => {
          if (rt.parentId === this.selectedPrimaryRisk.riskCategoryId) {
            riskTypesSub.push(rt);
          }
        });

        if (lodashGet(this.params, "riskSubCategories")) {
          const riskSubCategories = lodashGet(
            this.params,
            "riskSubCategories"
          ).split(",");
          lodashRemove(riskTypesSub, (r) => {
            return !riskSubCategories.includes(r.id);
          });
        }

        let subRatings = [];
        for (let i = 0; i < this.ratings.length; i++) {
          const rating = this.ratings[i];
          if (
            lodashGet(rating, "riskCategoryId") &&
            lodashGet(rating, "rateValue")
          ) {
            riskTypesSub.forEach((riskType) => {
              if (rating.riskCategoryId === riskType.id) {
                subRatings.push({
                  id: riskType.id,
                  type: riskType.name.replace(/\//g, " / "),
                  display: rating.riskCategoryNameDisplay,
                  value: rating.rateValue,
                  level: rating.rateName,
                  parentId: riskType.parentId,
                });
              }
            });
          }
        }
        subRatings = lodashOrderBy(subRatings, ["type"], ["asc"]);
        this.risksSub = subRatings;
      }
    },
  },
  watch: {
    ratings: {
      async handler() {
        if (this.ratings && Array.isArray(this.ratings)) {
          this.riskTypes = await this.getFilteredRiskTypes();
          this.risks = [];
          for (let i = 0; i < this.ratings.length; i++) {
            const rating = this.ratings[i];
            if (
              lodashGet(rating, "riskCategoryId") &&
              lodashGet(rating, "rateValue")
            ) {
              this.riskTypesPrimary.forEach((riskType) => {
                if (rating.riskCategoryId === riskType.id) {
                  this.risks.push({
                    id: riskType.id,
                    type: riskType.name,
                    value: rating.rateValue,
                  });
                }
              });
            }
          }
          this.risks = lodashOrderBy(this.risks, ["order"], ["asc"]);
          this.getSubRatings();
        }
      },
      immediate: true,
    },
    selectedPrimaryRisk() {
      this.getSubRatings();
    },
  },
  beforeDestroy() {},
};
</script>

<style scoped>
.details-subheading {
  font-size: 15px;
}
.subrisk-container {
  font-size: 12px;
  border-radius: 4px;
}

.hyp-risk-icon {
  width: 100%;
  border-radius: 10px;
}
.hyp-risk-type {
  text-transform: capitalize;
}
.hyp-risk-grid-2 {
  display: grid;
  grid-template-columns: 50% 50%;
}
</style>
